import { useCallback } from 'react';
import type { Highlight } from '../types';

type SaveHandler = ((highlights: Highlight[]) => void | Promise<void>) | undefined;

interface UseHighlightActionsOptions {
    onSaveRef?: React.MutableRefObject<SaveHandler>;
    highlightsRef: React.MutableRefObject<Highlight[]>;
    pageNumber: number;
    setHighlights: React.Dispatch<React.SetStateAction<Highlight[]>>;
    setActivePopup: React.Dispatch<React.SetStateAction<any>>;
    pendingSelection: { startOffset: number; endOffset: number; text: string } | null;
    setPendingSelection: React.Dispatch<React.SetStateAction<{ startOffset: number; endOffset: number; text: string } | null>>;
    pendingComment: string;
    setPendingComment: React.Dispatch<React.SetStateAction<string>>;
    pendingPolygon: Array<{ x: number; y: number }> | null;
    setPendingPolygon: React.Dispatch<React.SetStateAction<Array<{ x: number; y: number }> | null>>;
    deactivateMode: () => void;
    pendingRect: { x1: number; y1: number; x2: number; y2: number } | null;
    setPendingRect: React.Dispatch<React.SetStateAction<{ x1: number; y1: number; x2: number; y2: number } | null>>;
    pendingMarker: { x: number; y: number } | null;
    setPendingMarker: React.Dispatch<React.SetStateAction<{ x: number; y: number } | null>>;
    pendingIsCritical: boolean;
    setPendingIsCritical: React.Dispatch<React.SetStateAction<boolean>>;
    pendingCodeId: number | undefined;
    setPendingCodeId: React.Dispatch<React.SetStateAction<number | undefined>>;
    editingHighlight: { id: string; note: string } | null;
    setEditingHighlight: React.Dispatch<React.SetStateAction<{ id: string; note: string } | null>>;
    editingComment: string;
    setEditingComment: React.Dispatch<React.SetStateAction<string>>;
    editingIsCritical: boolean;
    setEditingIsCritical: React.Dispatch<React.SetStateAction<boolean>>;
    editingCodeId: number | undefined;
    setEditingCodeId: React.Dispatch<React.SetStateAction<number | undefined>>;
}

function isUnsavedLocal(h: Highlight): boolean {
    return !h.detail?.noteId && !/^\d+-\d+$/.test(h.id);
}

function markPersist(
    list: Highlight[],
    ids: Set<string>,
    status: 'saving' | 'failed',
): Highlight[] {
    return list.map((h) => (ids.has(h.id) ? { ...h, persistStatus: status } : h));
}

export function useHighlightActions({
    onSaveRef,
    highlightsRef,
    pageNumber,
    setHighlights,
    setActivePopup,
    pendingSelection,
    setPendingSelection,
    pendingComment,
    setPendingComment,
    pendingPolygon,
    setPendingPolygon,
    deactivateMode,
    pendingRect,
    setPendingRect,
    editingHighlight,
    setEditingHighlight,
    editingComment,
    setEditingComment,
    pendingMarker,
    setPendingMarker,
    pendingIsCritical,
    setPendingIsCritical,
    pendingCodeId,
    setPendingCodeId,
    editingIsCritical,
    setEditingIsCritical,
    editingCodeId,
    setEditingCodeId,
}: UseHighlightActionsOptions) {

    const applyList = useCallback((updated: Highlight[]) => {
        highlightsRef.current = updated;
        setHighlights(updated);
    }, [highlightsRef, setHighlights]);

    /** Вызов onSave; при ошибке помечает локальные UUID-заметки как failed. */
    const persistList = useCallback((updated: Highlight[], failedIds?: Set<string>) => {
        const idsToFail = failedIds ?? new Set(updated.filter(isUnsavedLocal).map((h) => h.id));
        const result = onSaveRef?.current?.(updated);
        return Promise.resolve(result).catch(() => {
            // Ошибка уже отражена в saveStatus модалки; здесь — per-note UI.
            applyList(markPersist(highlightsRef.current, idsToFail, 'failed'));
        });
    }, [onSaveRef, applyList, highlightsRef]);

    const commitHighlight = useCallback((highlight: Highlight, cleanup?: () => void) => {
        const withStatus: Highlight = { ...highlight, persistStatus: 'saving' };
        const updated = [...highlightsRef.current, withStatus];
        applyList(updated);
        setPendingComment('');
        setPendingIsCritical(false);
        setPendingCodeId(undefined);
        cleanup?.();
        void persistList(updated, new Set([withStatus.id]));
    }, [highlightsRef, applyList, setPendingComment, setPendingIsCritical, setPendingCodeId, persistList]);

    const makeCommit = useCallback(
        <T,>(
            pending: T | null,
            setPending: React.Dispatch<React.SetStateAction<T | null>>,
            toHighlight: (data: T) => Highlight,
            cleanup?: () => void,
        ) =>
            () => {
                if (!pending) return;
                commitHighlight(toHighlight(pending), () => {
                    setPending(null);
                    cleanup?.();
                });
            },
        [commitHighlight],
    );

    const makeCancel = useCallback(
        <T,>(setPending: React.Dispatch<React.SetStateAction<T | null>>) =>
            () => {
                setPending(null);
                setPendingComment('');
            },
        [setPendingComment],
    );

    const updateHighlightNote = useCallback((id: string, note: string, isCritical?: boolean, codeId?: number) => {
        const prev = highlightsRef.current;
        const target = prev.find((h) => h.id === id);
        if (!target) return;
        const noteChanged = (target.note ?? '') !== note;
        const criticalChanged = isCritical !== undefined && target.isCritical !== isCritical;
        const codeChanged = codeId !== undefined && target.codeId !== codeId;
        if (!noteChanged && !criticalChanged && !codeChanged) return;
        const noteId = target.detail?.noteId;
        const isSameNote = (h: Highlight) =>
            h.id === id || (noteId != null && h.detail?.noteId === noteId);
        const updated = prev.map((h) => (isSameNote(h)
            ? {
                ...h,
                note,
                isCritical: isCritical !== undefined ? isCritical : h.isCritical,
                codeId: codeId !== undefined ? codeId : h.codeId,
                persistStatus: isUnsavedLocal(h) ? 'saving' as const : h.persistStatus,
                detail: h.detail ? { ...h.detail, titleHtml: note, isCritical: isCritical !== undefined ? isCritical : h.detail.isCritical } : h.detail,
            }
            : h));
        applyList(updated);
        void persistList(updated, new Set(updated.filter(isUnsavedLocal).map((h) => h.id)));
    }, [highlightsRef, applyList, persistList]);

    const deleteHighlight = useCallback((id: string) => {
        const updated = highlightsRef.current.filter((h) => h.id !== id);
        applyList(updated);
        void persistList(updated);
        setActivePopup(null);
    }, [highlightsRef, applyList, persistList, setActivePopup]);

    /** Повторная отправка несохранённой (failed) заметки. */
    const retryPersistHighlight = useCallback((id: string) => {
        const target = highlightsRef.current.find((h) => h.id === id);
        if (!target || !isUnsavedLocal(target)) return;
        const updated = markPersist(highlightsRef.current, new Set([id]), 'saving');
        applyList(updated);
        void persistList(updated, new Set([id]));
    }, [highlightsRef, applyList, persistList]);

    const commitPendingSelection = makeCommit(
        pendingSelection, setPendingSelection,
        (sel) => ({
            id: crypto.randomUUID(), type: 'text', pageNumber,
            startOffset: sel.startOffset, endOffset: sel.endOffset, text: sel.text,
            note: pendingComment, isCritical: pendingIsCritical, codeId: pendingCodeId,
            createdAt: new Date().toISOString(),
        }),
    );

    const cancelPendingSelection = makeCancel(setPendingSelection);

    const commitPendingPolygon = makeCommit(
        pendingPolygon, setPendingPolygon,
        (pts) => ({
            id: crypto.randomUUID(), type: 'polygon', pageNumber,
            points: pts, note: pendingComment, isCritical: pendingIsCritical, codeId: pendingCodeId,
            createdAt: new Date().toISOString(),
        }),
        () => deactivateMode(),
    );

    const cancelPendingPolygon = makeCancel(setPendingPolygon);

    const commitPendingRect = makeCommit(
        pendingRect, setPendingRect,
        (r) => ({
            id: crypto.randomUUID(), type: 'rect', pageNumber,
            x1: r.x1, y1: r.y1, x2: r.x2, y2: r.y2,
            note: pendingComment, isCritical: pendingIsCritical, codeId: pendingCodeId,
            createdAt: new Date().toISOString(),
        }),
        () => deactivateMode(),
    );

    const cancelPendingRect = makeCancel(setPendingRect);

    const commitPendingMarker = makeCommit(
        pendingMarker, setPendingMarker,
        (m) => ({
            id: crypto.randomUUID(), type: 'positioned', pageNumber,
            x: m.x, y: m.y, note: pendingComment, isCritical: pendingIsCritical, codeId: pendingCodeId,
            createdAt: new Date().toISOString(),
        }),
    );

    const cancelPendingMarker = makeCancel(setPendingMarker);

    const commitEditModal = useCallback(() => {
        if (!editingHighlight) return;
        updateHighlightNote(editingHighlight.id, editingComment, editingIsCritical, editingCodeId);
        setEditingHighlight(null);
        setEditingComment('');
    }, [editingHighlight, editingComment, editingIsCritical, editingCodeId, updateHighlightNote, setEditingHighlight, setEditingComment]);

    const cancelEditModal = useCallback(() => {
        setEditingHighlight(null);
        setEditingComment('');
    }, [setEditingHighlight, setEditingComment]);

    return {
        updateHighlightNote,
        deleteHighlight,
        retryPersistHighlight,
        commitPendingSelection,
        cancelPendingSelection,
        commitPendingPolygon,
        cancelPendingPolygon,
        commitPendingRect,
        cancelPendingRect,
        commitPendingMarker,
        cancelPendingMarker,
        commitEditModal,
        cancelEditModal,
    };
}
